import React from "react";
import { UpdateUserSettingsFormData, UpdateUserSettingsFormDataKey } from "src/types";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { UserEmailSettingsToolTip, SubmissionTooltip } from "src/components/tooltips";
import { TextInputBox } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";

interface UserSettingsFormProps {
  formData: UpdateUserSettingsFormData;
  validationErrors?: ValidationErrors<UpdateUserSettingsFormDataKey>;
  formLvlErrorMessage?: string;
  isFormUpdated: boolean;
  handleInputChange: (
    field: UpdateUserSettingsFormDataKey,
    value: UpdateUserSettingsFormData[keyof UpdateUserSettingsFormData]
  ) => void;
  handleSubmit: (e: React.FormEvent) => void;
}

const UserSettingsForm: React.FC<UserSettingsFormProps> = ({
  formData,
  validationErrors,
  formLvlErrorMessage,
  isFormUpdated,
  handleInputChange,
  handleSubmit
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto space-y-8">
        {/* Header Section */}
        <div className="bg-black rounded-2xl shadow-soft px-8 py-6">
          <h1 className="text-3xl font-bold text-white tracking-tight">Settings</h1>
          <p className="text-primary-100 mt-2 text-lg">Update your user settings</p>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="space-y-8">
          {/* Settings Card */}
          <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
            <div className="p-6 grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Name</label>
                <TextInputBox
                  id="user-name"
                  inputValue={formData.name}
                  onInputChange={(value: string) => handleInputChange("name", value)}
                  maxLength={30}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("name")} />
              </div>

              <div className="space-y-2">
                <label className="flex items-center text-sm font-medium text-secondary-700 mb-2">
                  Email Greeting
                  <div className="ml-2">
                    <UserEmailSettingsToolTip />
                  </div>
                </label>
                <TextInputBox
                  id="email-greeting"
                  inputValue={formData.emailGreeting}
                  onInputChange={(value: string) => handleInputChange("emailGreeting", value)}
                  maxLength={10}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("emailGreeting")} />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Purchase Order Email Body</label>
                <TextInputBox
                  id="email-body"
                  inputValue={formData.purchaseOrderEmailBody}
                  onInputChange={(value: string) => handleInputChange("purchaseOrderEmailBody", value)}
                  maxLength={1500}
                  multiline={true}
                  rows={10}
                />
                <ValidationErrorMessage
                  message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderEmailBody")}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 mb-2">Email Valediction</label>
                <TextInputBox
                  id="email-valediction"
                  inputValue={formData.emailValediction}
                  onInputChange={(value: string) => handleInputChange("emailValediction", value)}
                  maxLength={40}
                  multiline={true}
                  rows={3}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("emailValediction")} />
              </div>
            </div>
          </div>

          <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-start items-center ">
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <button
                className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  !isFormUpdated
                    ? "bg-secondary-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:ring-primary-500 shadow-lg hover:shadow-xl"
                }`}
                disabled={!isFormUpdated}
                type="submit"
              >
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Settings
                </span>
              </button>
            </div>

            <div className="flex-shrink-0">{!isFormUpdated && <SubmissionTooltip formState={"NO_CHANGE"} />}</div>
          </div>
        </form>
      </div>
    </div>
  );
};

export { UserSettingsForm };
